// Hydrates every [data-impact-message] on the page — the theme block
// (blocks/impact-message.liquid) and any Builder.io Custom Code block that
// uses the same markup. Loaded on every page from layout/theme.liquid.
//
// Contract (data attributes on the root):
//   data-personal-template           logged in, one funded charity.
//   data-personal-template-multiple  logged in, several charities.
//   data-overall-template            overall figure; [total] is replaced live.
//   data-first-name                  optional; the API's first_name wins.
//   data-impact-asterisk="true"      append "*" to the metric if it has none.
// Tokens: [name] [impact] (bold) [cause] [total].
// Views: [data-impact-message-view="personal" | "overall"]; the overall one
// holds [data-impact-message-figure] and [data-impact-message-figure-part].

(function () {
  if (window.__impactMessageLoaded) return;
  window.__impactMessageLoaded = true;

  var personalRequest = null;

  // layout/theme.liquid loads the stylesheet async, so a view revealed before
  // it lands paints as unstyled text. Resolved either way after 3s rather than
  // holding the strip hostage to a stylesheet that never arrives.
  var stylesReady = new Promise(function (resolve) {
    var link = document.querySelector('link[href*="impact-message.css"]');
    if (!link || link.sheet) return resolve();
    link.addEventListener('load', resolve, { once: true });
    link.addEventListener('error', resolve, { once: true });
    setTimeout(resolve, 3000);
  });

  function loadPersonal() {
    // Shared with impact-bar / my-causes when one of them is on the page.
    if (window.PersonalImpact) return window.PersonalImpact.load();

    if (!personalRequest) {
      // App Proxy: Shopify adds and signs the logged-in customer's id.
      var url = '/apps/olivela-api/personal-referral-impact';

      personalRequest = fetch(url, { headers: { Accept: 'application/json' } }).then(function (response) {
        if (!response.ok) throw new Error('Request failed: ' + response.status);
        return response.json();
      });
      personalRequest.catch(function () {
        personalRequest = null;
      });
    }
    return personalRequest;
  }

  // The promo header ticker (.impact-banner-heading in
  // assets/impact-banner-ticker.js) shows days_of_school x 3, the dollars
  // raised; days_of_school itself is the hours of change this strip words it
  // as. Same feed, so the two never disagree on the underlying figure.
  /** 1,616,420 -> "1.62 million", 1,600,000 -> "1.6 million", 840,000 -> "840,000". */
  function totalFrom(data) {
    var hours = data && parseInt(data.days_of_school, 10);
    if (!(hours > 0)) return '';

    if (hours >= 1e6) {
      var millions = Math.round((hours / 1e6) * 100) / 100;
      return millions + ' million';
    }
    return hours.toLocaleString('en-US');
  }

  function unitsOf(units, text) {
    var number = Number(units);
    if (units != null && units !== '' && isFinite(number)) return number;
    var match = /^\s*([\d,.]+)/.exec(String(text || ''));
    number = match ? Number(match[1].replace(/,/g, '')) : 0;
    return isFinite(number) ? number : 0;
  }

  /** "23,300 hours of change*": the API's figure with its own wording, asterisk included. */
  function impactOf(data, asterisk) {
    var text = String(data.personal_impact_text || '').trim();
    var units = unitsOf(data.personal_impact_units, text);
    if (!(units > 0) || !text) return '';

    var match = /^([\d,.]+)\s*(.*)$/.exec(text);
    var wording = match ? match[2].trim() : text;
    
    // The universal metric reads as days of school here; a charity's own
    // wording ("student e-learning modules") is left as sent.
    wording = wording.replace(/\bhours of change\b/i, 'days of school');
    
    var impact = (units.toLocaleString('en-US') + ' ' + wording).trim();

    // Opt-in: the metric has to carry the footnote mark even when the API
    // text leaves it off.
    if (asterisk && impact.slice(-1) !== '*') impact += '*';
    return impact;
  }

  /**
   * The charities the customer has actually funded, counted from both
   * cause_summary and purchase_history and merged by cause code — so a
   * several-cause customer is never undercounted (and wrongly shown the
   * one-cause "through [cause]" line) when either source is empty, sparse,
   * or carries only some of the causes.
   */
  function fundedCauses(data) {
    var byCode = {};

    function add(code, name, units) {
      code = String(code || '').toLowerCase();
      if (!code) return;
      if (!byCode[code]) byCode[code] = { cause_short_code: code, charity_name: '', total_units: 0 };
      byCode[code].total_units += units;
      if (!byCode[code].charity_name && name) byCode[code].charity_name = String(name);
    }

    (Array.isArray(data.cause_summary) ? data.cause_summary : []).forEach(function (entry) {
      if (!entry) return;
      add(entry.cause_short_code, entry.charity_name, unitsOf(entry.total_units, entry.total_text));
    });

    (Array.isArray(data.purchase_history) ? data.purchase_history : []).forEach(function (purchase) {
      if (!purchase) return;
      add(purchase.cause_short_code, purchase.charity_name, unitsOf(purchase.benefit_unit, purchase.benefit_text));
    });

    return Object.keys(byCode)
      .map(function (code) {
        return byCode[code];
      })
      .filter(function (entry) {
        return entry.total_units > 0;
      });
  }

  /**
   * Writes `template` into `el`, swapping each [token] for its value as a text
   * node (names are never parsed as HTML). Only [impact] is bold.
   */
  function fill(el, template, values) {
    el.textContent = '';
    template.split(/(\[(?:name|impact|cause|total)\])/).forEach(function (part) {
      var token = /^\[(\w+)\]$/.exec(part);
      if (!token) {
        if (part) el.appendChild(document.createTextNode(part));
        return;
      }

      var value = values[token[1]];
      if (!value) return;

      if (token[1] === 'impact') {
        var strong = document.createElement('strong');
        strong.textContent = value;
        el.appendChild(strong);
      } else {
        el.appendChild(document.createTextNode(value));
      }
    });
  }

  function init(root) {
    if (root.dataset.impactMessageInit === 'true') return;
    root.dataset.impactMessageInit = 'true';

    var overallTemplate = root.dataset.overallTemplate || '';
    var asterisk = root.dataset.impactAsterisk === 'true';
    var loggedIn = window.IS_LOGGED_IN === true && !!window.CUSTOMER_ID;

    function show(name) {
      stylesReady.then(function () {
        root.querySelectorAll('[data-impact-message-view]').forEach(function (el) {
          el.hidden = el.dataset.impactMessageView !== name;
        });
      });
    }

    /* ── Overall giveback (everyone) ── */

    function setOverall(data) {
      var total = totalFrom(data);
      var figure = root.querySelector('[data-impact-message-figure]');
      if (!total || !figure) return;

      fill(figure, overallTemplate, { total: total });
      root.querySelectorAll('[data-impact-message-figure-part]').forEach(function (el) {
        el.hidden = false;
      });
    }

    // Same feed as the top banner (assets/impact-banner-ticker.js), so no
    // request of its own: its global, its one-day cache, or its event.
    if (overallTemplate.indexOf('[total]') !== -1) {
      if (window.OlivelaImpactBenefit) {
        setOverall(window.OlivelaImpactBenefit);
      } else {
        try {
          var cached = JSON.parse(localStorage.getItem('impact_benefit_data') || 'null');
          if (cached && cached.data) setOverall(cached.data);
        } catch (error) {}
        document.addEventListener('olivela:impact-benefit', function (event) {
          setOverall(event.detail);
        });
      }
    }

    if (!loggedIn) {
      // Said rather than assumed, so markup that cannot run Liquid to pick a
      // starting view (Builder.io) still lands on the right one.
      show('overall');
      return;
    }

    /* ── The customer's own giveback ── */

    function renderPersonal(data) {
      var impact = impactOf(data, asterisk);
      var target = root.querySelector('[data-impact-message-personal-text]');
      if (!impact || !target) return false;

      var funded = fundedCauses(data);
      var several = funded.length > 1;
      var template = (several && root.dataset.personalTemplateMultiple) || root.dataset.personalTemplate || '';
      var cause = '';

      if (!several) {
        var current = data.cause || {};
        cause = String((funded[0] && funded[0].charity_name) || current.name || current.short_code || '').trim();
      }

      // Only a real charity is named: several causes, or a single one we can't
      // name, drop "through [cause]" rather than filling it with a stand-in.
      if (!cause) {
        template = template.replace(/\s*\bthrough\s*\[cause\]/gi, '');
      }

      var name = String(data.first_name || root.dataset.firstName || '').trim();
      // Whatever case the API or the storefront's own first_name arrives in.
      if (name) name = name.charAt(0).toUpperCase() + name.slice(1);
      if (!name) {
        // Drop "[name], " and start the sentence with a capital.
        template = template.replace(/\[name\][,\s]*/g, '').trim();
        template = template.charAt(0).toUpperCase() + template.slice(1);
      }

      fill(target, template, { name: name, impact: impact, cause: cause });
      return true;
    }

    // Nothing shows for a logged-in customer until this settles; no donations
    // yet, or a failed request, falls back to the overall message.
    show(null);

    // Better than a blank strip while a slow request is still out — and it
    // gives way to the real answer rather than latching.
    var giveUp = setTimeout(function () {
      show('overall');
    }, 5000);

    loadPersonal()
      .then(function (data) {
        clearTimeout(giveUp);
        show(data && renderPersonal(data) ? 'personal' : 'overall');
      })
      .catch(function (error) {
        clearTimeout(giveUp);
        console.error('[impact-message]', error);
        show('overall');
      });
  }

  var scanFrame = 0;

  function scan() {
    scanFrame = 0;
    document.querySelectorAll('[data-impact-message]').forEach(init);
  }

  function scheduleScan() {
    if (!scanFrame) scanFrame = requestAnimationFrame(scan);
  }

  // Markup that arrives on its own terms (Builder.io) calls this rather than
  // waiting to be noticed.
  window.ImpactMessage = { scan: scheduleScan };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scan);
  } else {
    scan();
  }

  // Builder.io mounts its custom-code blocks whenever its own bundle gets
  // round to it, which on a heavy page is well past any fixed deadline — so
  // this keeps watching. Each burst of mutations costs one querySelectorAll on
  // the next frame.
  new MutationObserver(scheduleScan).observe(document.documentElement, {
    childList: true,
    subtree: true
  });

  document.addEventListener('shopify:section:load', scheduleScan);
})();
